import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LogoText } from "../../../assets/icons";
import { useIsMobile } from "../../../hooks/customDeviceDetect";
import { cn } from "../../../util/classNameConstructor";
import Button from "../../core/button/Button";

const AccessibilityStatement = ({ handleNavigateBack, project }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (project?.title) {
      document.title = `${project.title} – ${t("accessibilityStatement:title")}`;
    } else {
      document.title = t("accessibilityStatement:title");
    }
  }, [project?.title, t]);

  return (
    <div className="h-screen w-screen overflow-y-auto bg-[#fffefc]">
      <div className="ml-12 mt-7">
        <LogoText transform="scale(2)" />
      </div>
      <div className="mx-auto mt-48 max-w-4xl px-4 py-8">
        <Button
          variant="outline"
          leadingIcon="arrow-left-md"
          text={t("global:back")}
          onClick={handleNavigateBack}
        />

        <h1
          className={cn(
            "my-6 font-black",
            isMobile ? "heading-2xl" : "heading-5xl",
          )}
        >
          {t("accessibilityStatement:title")}
        </h1>

        <div className="mb-8">
          <h2 className="heading-xl mb-4 font-bold">
            {t("accessibilityStatement:subtitle")}
          </h2>
          <p className="body-lg mb-4 italic">
            {t("accessibilityStatement:date")}
          </p>
        </div>

        <section className="mb-8">
          <h2 className="heading-xl mb-4">
            1. {t("accessibilityStatement:scope_title")}
          </h2>
          <p className="body-lg mb-4">
            {t("accessibilityStatement:scope_description")}
          </p>
          <p className="body-lg mb-4">
            {t("accessibilityStatement:scope_modules")}
          </p>
          <ul className="body-lg mb-4 ml-6 list-disc">
            <li>{t("accessibilityStatement:scope_modules_info")}</li>
            <li>{t("accessibilityStatement:scope_modules_dialog")}</li>
            <li>{t("accessibilityStatement:scope_modules_survey")}</li>
            <li>{t("accessibilityStatement:scope_modules_navigation")}</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="heading-xl mb-4">
            2. {t("accessibilityStatement:legal_basis_title")}
          </h2>
          <p className="body-lg mb-4">
            {t("accessibilityStatement:legal_basis_description")}
          </p>
        </section>

        <section className="mb-8">
          <h2 className="heading-xl mb-4">
            3. {t("accessibilityStatement:conformance_status")}
          </h2>
          <p className="body-lg mb-4">
            {t("accessibilityStatement:conformance_details")}
          </p>
          <p className="body-lg mb-4">
            {t("accessibilityStatement:last_audit")}
          </p>
          <p className="body-lg mb-4">
            {t("accessibilityStatement:user_testing")}
          </p>
        </section>

        <section className="mb-8">
          <h2 className="heading-xl mb-4">
            4. {t("accessibilityStatement:implementation_title")}
          </h2>
          <p className="body-lg mb-4">
            {t("accessibilityStatement:implementation_description")}
          </p>

          <div className="mb-6">
            <h3 className="heading-lg mb-2 font-semibold">
              <strong>
                {t("accessibilityStatement:keyboard_accessibility")}
              </strong>
            </h3>
            <p className="body-lg mb-4">
              {t("accessibilityStatement:keyboard_accessibility_details")}
            </p>
          </div>

          <div className="mb-6">
            <h3 className="heading-lg mb-2 font-semibold">
              <strong>
                {t("accessibilityStatement:screenreader_compatibility")}
              </strong>
            </h3>
            <p className="body-lg mb-4">
              {t("accessibilityStatement:screenreader_compatibility_details")}
            </p>
          </div>

          <div className="mb-6">
            <h3 className="heading-lg mb-2 font-semibold">
              <strong>{t("accessibilityStatement:contrast_visual")}</strong>
            </h3>
            <p className="body-lg mb-4">
              {t("accessibilityStatement:contrast_visual_details")}
            </p>
            <p className="body-lg mb-4 italic">
              {t("accessibilityStatement:contrast_visual_note")}
            </p>
          </div>

          <div className="mb-6">
            <h3 className="heading-lg mb-2 font-semibold">
              <strong>{t("accessibilityStatement:forms_surveys")}</strong>
            </h3>
            <p className="body-lg mb-4">
              {t("accessibilityStatement:forms_surveys_details")}
            </p>
          </div>

          <div className="mb-6">
            <h3 className="heading-lg mb-2 font-semibold">
              <strong>{t("accessibilityStatement:map_modules")}</strong>
            </h3>
            <p className="body-lg mb-4">
              {t("accessibilityStatement:map_modules_details")}
            </p>
          </div>

          <div className="mb-6">
            <h3 className="heading-lg mb-2 font-semibold">
              <strong>{t("accessibilityStatement:user_media_content")}</strong>
            </h3>
            <p className="body-lg mb-4">
              {t("accessibilityStatement:user_media_content_details")}
            </p>
            <p className="body-lg mb-4">
              {t("accessibilityStatement:user_media_content_responsibility")}
            </p>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="heading-xl mb-4">
            5. {t("accessibilityStatement:enhanced_mode_title")}
          </h2>
          <p className="body-lg mb-4">
            {t("accessibilityStatement:enhanced_mode_description")}
          </p>
          <ul className="body-lg mb-4 ml-6 list-disc">
            <li>
              <strong>
                {t("accessibilityStatement:enhanced_mode_large_text")}
              </strong>
            </li>
            <li>
              <strong>
                {t("accessibilityStatement:enhanced_mode_simple_language")}
              </strong>
            </li>
            <li>
              <strong>
                {t("accessibilityStatement:enhanced_mode_read_aloud")}
              </strong>
            </li>
          </ul>
          <p className="body-lg mb-4">
            {t("accessibilityStatement:enhanced_mode_future")}
          </p>
        </section>

        <section className="mb-8">
          <h2 className="heading-xl mb-4">
            6. {t("accessibilityStatement:known_limitations_title")}
          </h2>
          <p className="body-lg mb-4">
            {t("accessibilityStatement:known_limitations_description")}
          </p>
          <p className="body-lg mb-4">
            {t("accessibilityStatement:known_limitations_process")}
          </p>
        </section>

        <section className="mb-8">
          <h2 className="heading-xl mb-4">
            7. {t("accessibilityStatement:compatibility")}
          </h2>
          <p className="body-lg mb-4">
            {t("accessibilityStatement:compatibility_details")}
          </p>
          <p className="body-lg mb-4">
            {t("accessibilityStatement:compatibility_note")}
          </p>
        </section>

        <section className="mb-8">
          <h2 className="heading-xl mb-4">
            8. {t("accessibilityStatement:contact_feedback_title")}
          </h2>
          <p className="body-lg mb-4">
            {t("accessibilityStatement:contact_feedback_description")}
          </p>
          <p className="body-lg mb-4">
            {t("accessibilityStatement:contact_feedback_response")}
          </p>
          <p className="body-lg mb-4 font-semibold">
            <strong>{t("accessibilityStatement:contact_email")}</strong>
          </p>
        </section>

        <section className="mb-8">
          <h2 className="heading-xl mb-4">
            9. {t("accessibilityStatement:responsibility_title")}
          </h2>
          <p className="body-lg mb-4">
            {t("accessibilityStatement:responsibility_description")}
          </p>
          <p className="body-lg mb-4">
            {t("accessibilityStatement:responsibility_process")}
          </p>
        </section>

        <section className="mb-8">
          <h2 className="heading-xl mb-4">
            10. {t("accessibilityStatement:enforcement_title")}
          </h2>
          <p className="body-lg mb-4">
            {t("accessibilityStatement:enforcement_description")}
          </p>
          <p className="body-lg mb-4">
            {t("accessibilityStatement:enforcement_link_text")}{" "}
            <a
              href="https://www.bfit-bund.de/DE/Service/Kontakt/Node.html"
              className="text-blue-600 underline hover:text-blue-800"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("accessibilityStatement:enforcement_link_url")}
            </a>
          </p>
        </section>

        <p className="body-xs text-right">
          {t("accessibilityStatement:last_updated", { date: "Juni 2025" })}
        </p>
      </div>
    </div>
  );
};

export default AccessibilityStatement;
